import { Dialog, elWFull } from '@reapit/elements'
import { FC, useEffect } from 'react'
import { UseModalIframeType } from '../utils'

interface ModalIframeProps extends UseModalIframeType {}

/**
 * `ModalIframe` is a component that displays an iframe inside a dialog modal.
 * It allows controlling the visibility of the modal and loading a URL into the iframe.
 *
 * Props:
 * @param title (string): The title of the modal dialog.
 * @param iframeLink (string): The URL to load into the iframe when the modal is visible.
 * @param isModalVisible (boolean): Determines if the modal iframe is visible or not.
 * @param enableModalVisibility (function): Function to show the modal and load the iframe with the given URL.
 * @param disableModalVisibility (function): Function to close the modal when triggered.
 *
 * This component:
 * 1. Renders a `Dialog` containing an `iframe`.
 * 2. Displays a close button that calls `disableModalVisibility` to hide the modal.
 * 3. Loads the `iframeLink` in the iframe when the modal is visible.
 * 4. Ensures the iframe takes up full width and 50% of the viewport height.
 */
export const ModalIframe: FC<ModalIframeProps> = ({ isModalVisible, disableModalVisibility, title, iframeLink }) => {
  // To remove: When support is added for keyboard accessibility in Elements for Universal Search
  // This useEffect listens for the Escape key to close the modal when it is visible.
  // It is useful for improving user experience by allowing quick dismissal of the modal.
  // Note: This is a temporary solution until keyboard accessibility is fully implemented in the Elements library
  // and should be removed once that is done.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalVisible) {
        disableModalVisibility()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isModalVisible, disableModalVisibility])

  return (
    <Dialog
      isOpen={isModalVisible}
      style={{ padding: 0, width: '820px', maxWidth: '820px', height: '675px' }}
      size="medium"
      onClose={disableModalVisibility}
    >
      <Dialog.Header action={<Dialog.HeaderCloseButton />} aria-label={title}>
        {title}
      </Dialog.Header>
      <Dialog.Body>
        {isModalVisible && (
          <iframe
            className={elWFull}
            style={{ width: '100%', height: '580px', border: '0px' }}
            src={iframeLink}
          ></iframe>
        )}
      </Dialog.Body>
    </Dialog>
  )
}
